/**
 * The garage-door family's own reading and write — one fold for the widget's
 * body, its tile, its chips and the write they drive.
 *
 * The hub's class (`otto/src/device/classes/unified/security.ts`,
 * `classId: 'garage_door'`) declares exactly two values:
 *
 *   · `position` — Status, `settable: false`. It arrives in two shapes: a
 *     percentage (the class' own `unit: '%'`) or a boolean (the handler's
 *     `DPT1.009 door_position_status`, whose own note reads *"false = closed"*),
 *     and `settings.status_type` decides which one a device carries.
 *   · `command`  — Control, `settable: true`, the value the motor is driven
 *     through (`GarageDoorHandler.onUserWrite` → `sendToIntegration('command',
 *     value)`); the handler itself drives it with `false` to close and `0` to
 *     stop after the safety timeout, and `settings.garage_door_type` decides
 *     whether one trigger address carries it (`pulse`) or an up/down pair does.
 *
 * The convention here — and the one the 3D family is built to — is that
 * `position` reads as **how far the door is OPEN**: 0 % = closed, 100 % = fully
 * open. A door is not a blind (a cover reports 0 = up, 100 = down), which is why
 * this fold exists instead of the covers' one.
 */

/** One device-value row as the door's fold reads it. */
export type GarageDoorValueRow = {
  valueKey?: unknown;
  property_key?: unknown;
  valueType?: unknown;
  currentValue?: unknown;
};

export const GARAGE_DOOR_POSITION_KEY = 'position';
export const GARAGE_DOOR_COMMAND_KEY = 'command';

/** The door's own state word — `moving` is any reading between the two ends. */
export type GarageDoorState = 'open' | 'closed' | 'moving' | 'unknown';

/** What the operator asked for (a stop is only meaningful mid-move). */
export type GarageDoorAction = 'open' | 'close' | 'stop';

/** The row's own key, whichever spelling it arrived under. */
function rowKey(row: GarageDoorValueRow): string {
  const key = typeof row.valueKey === 'string' && row.valueKey !== '' ? row.valueKey : row.property_key;
  return typeof key === 'string' ? key : '';
}

/** The row the fold reads, live bag first (the widget's socket subscription),
 *  else the rows' own value — the order `lockState` and the chrome both use. */
function rowOf(
  rows: readonly GarageDoorValueRow[] | null | undefined,
  live: Record<string, unknown>,
  key: string,
): GarageDoorValueRow | null {
  if (key in live) return { valueKey: key, currentValue: live[key] };
  return rows?.find((row) => rowKey(row) === key) ?? null;
}

/**
 * How far the door is open, 0…100 — or `null` when the device reports nothing.
 *
 * A number is the percentage the class declares (clamped, so a stray 255 from a
 * raw byte datapoint cannot read as 255 %). A boolean is the handler's own
 * shape, where `true` is open. Anything else — a missing row, a word — is
 * `null`, never a guess.
 */
export function garageDoorPercent(
  rows: readonly GarageDoorValueRow[] | null | undefined,
  live: Record<string, unknown> = {},
): number | null {
  const raw = rowOf(rows, live, GARAGE_DOOR_POSITION_KEY)?.currentValue;
  if (raw === true) return 100;
  if (raw === false) return 0;
  const value = typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : raw;
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.min(100, Math.max(0, Math.round(value)));
}

/** The state word a percentage reads as. */
export function garageDoorState(percent: number | null): GarageDoorState {
  if (percent === null) return 'unknown';
  if (percent >= 99) return 'open';
  if (percent <= 1) return 'closed';
  return 'moving';
}

/** Nearly open counts as open for a tile's on/off read (a half-open door reads on). */
export function isGarageDoorOpen(percent: number | null): boolean {
  return percent !== null && percent >= 50;
}

/**
 * The value to WRITE for an action, in the spelling the device's own `command`
 * row carries (the `lockWriteKey` discipline):
 *
 *   · a string row takes the word itself (`open` / `close` / `stop`) — the class'
 *     own shape, and the one this hub's demo device carries;
 *   · a number row takes `1` / `0` (DPT1.008-style up/down, and the `0` the
 *     handler sends for a safety stop);
 *   · a boolean row takes `true` / `false` (DPT1.001 pulse / DPT1.009 status).
 */
export function garageDoorCommandValue(
  rows: readonly GarageDoorValueRow[] | null | undefined,
  action: GarageDoorAction,
): unknown {
  const row = rows?.find((candidate) => rowKey(candidate) === GARAGE_DOOR_COMMAND_KEY) ?? null;
  const type = typeof row?.valueType === 'string' ? row.valueType.toLowerCase() : '';
  if (type === 'number') return action === 'open' ? 1 : 0;
  if (type === 'boolean') return action === 'open';
  // A string row — and a device whose row the widget has not seen yet: the word
  // is the class' own shape, and the handler forwards whatever it is given.
  if (type === 'string' || row === null) return action;
  // Any other declared type keeps the words too (the hub coerces per mapping).
  return action;
}

/** The door's own verbs, in both languages (the app's `tt()` pair shape). */
export const GARAGE_DOOR_ACTION_LABELS: Record<GarageDoorAction, { en: string; sl: string }> = {
  open: { en: 'Open', sl: 'Odpri' },
  close: { en: 'Close', sl: 'Zapri' },
  stop: { en: 'Stop', sl: 'Ustavi' },
};

/** The state words, in both languages. */
export const GARAGE_DOOR_STATE_LABELS: Record<GarageDoorState, { en: string; sl: string }> = {
  open: { en: 'Open', sl: 'Odprta' },
  closed: { en: 'Closed', sl: 'Zaprta' },
  moving: { en: 'Moving', sl: 'V gibanju' },
  unknown: { en: 'Unknown', sl: 'Neznano' },
};
